import { toISO } from './format.js'

const DAY = 86400000
const parse = (iso) => { const [y, m, d] = iso.split('-').map(Number); return new Date(y, m - 1, d) }

export const entries = (obj) => Object.entries(obj || {}).map(([id, v]) => ({ id, ...v }))

/**
 * Estimated loan balance on `onISO`: the confirmed balance compounds daily
 * (like the spreadsheet's D*1.0002105), and each monthly payment on paymentDay is subtracted.
 */
export function loanBalance(loan, onISO = toISO(new Date())) {
  if (!loan || loan.balance == null) return 0
  const start = parse(loan.asOf), end = parse(onISO)
  let bal = Number(loan.balance)
  const rate = Number(loan.dailyRate) || 0
  const pay = Number(loan.monthlyPayment) || 0
  for (let t = start.getTime() + DAY; t <= end.getTime(); t += DAY) {
    const d = new Date(t)
    bal *= 1 + rate
    if (pay && d.getDate() === Number(loan.paymentDay)) bal -= pay
  }
  return Math.max(0, bal)
}

export const annualToDaily = (annualPct) => Math.pow(1 + annualPct / 100, 1 / 365) - 1
export const dailyToAnnual = (daily) => (Math.pow(1 + daily, 365) - 1) * 100

/** Balance of an account, resolving synced sources from integrations. */
export function accountBalance(account, integrations = {}) {
  if (account.source === 'coinbase') return Number(integrations.coinbase?.total ?? account.balance ?? 0)
  if (account.source === 'lightyear') return Number(integrations.lightyear?.value ?? account.balance ?? 0)
  return Number(account.balance) || 0
}

export function netWorth(data, onISO) {
  const integrations = data.integrations || {}
  const accounts = entries(data.accounts)
  const byCategory = {}
  let assets = 0
  for (const a of accounts) {
    const b = accountBalance(a, integrations)
    assets += b
    byCategory[a.category] = (byCategory[a.category] || 0) + b
  }
  const debt = entries(data.loans).reduce((s, l) => s + loanBalance(l, onISO), 0)
  return { assets, debt, net: assets - debt, byCategory }
}

/** Income/expense totals grouped by YYYY-MM. */
export function monthlyTotals(transactions) {
  const out = {}
  for (const t of entries(transactions)) {
    const k = t.date.slice(0, 7)
    out[k] ||= { month: k, income: 0, expense: 0 }
    out[k][t.type === 'income' ? 'income' : 'expense'] += Number(t.amount) || 0
  }
  return Object.values(out).sort((a, b) => a.month.localeCompare(b.month))
}

export function categoryTotals(transactions, month, type = 'expense') {
  const out = {}
  for (const t of entries(transactions)) {
    if (t.type !== type || (month && !t.date.startsWith(month))) continue
    out[t.category || 'Other'] = (out[t.category || 'Other'] || 0) + (Number(t.amount) || 0)
  }
  return Object.entries(out).map(([category, amount]) => ({ category, amount })).sort((a, b) => b.amount - a.amount)
}

export function isRecurringActive(r, onISO) {
  if (r.paused) return false
  if (r.startDate && onISO < r.startDate) return false
  if (r.endDate && onISO > r.endDate) return false
  return true
}

/** Due date (ISO) of a recurring item within a month, clamped to the month's last day. */
export function dueDateInMonth(r, month) {
  const [y, m] = month.split('-').map(Number)
  const last = new Date(y, m, 0).getDate()
  const day = Math.min(Number(r.dayOfMonth) || 1, last)
  return `${month}-${String(day).padStart(2, '0')}`
}

/** Recurring items for `month` with whether a transaction was already posted from them. */
export function recurringForMonth(data, month) {
  const posted = new Set(entries(data.transactions).filter((t) => t.recurringId && t.date.startsWith(month)).map((t) => t.recurringId))
  return entries(data.recurring)
    .map((r) => ({ ...r, due: dueDateInMonth(r, month), posted: posted.has(r.id) }))
    .filter((r) => isRecurringActive(r, r.due))
    .sort((a, b) => a.due.localeCompare(b.due))
}

/** Upcoming unposted items from today through the next `days` days. */
export function upcoming(data, fromISO, days = 14) {
  const from = parse(fromISO)
  const until = toISO(new Date(from.getTime() + days * DAY))
  const months = [fromISO.slice(0, 7), until.slice(0, 7)]
  const seen = new Set()
  const out = []
  for (const m of months) {
    if (seen.has(m)) continue
    seen.add(m)
    for (const r of recurringForMonth(data, m)) if (!r.posted && r.due >= fromISO && r.due <= until) out.push(r)
  }
  return out
}

export function snapshotSeries(snapshots) {
  return Object.entries(snapshots || {})
    .map(([date, s]) => ({ date, assets: s.assets, debt: s.debt, net: s.assets - s.debt }))
    .sort((a, b) => a.date.localeCompare(b.date))
}
